import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Popover, Stack, TextField } from '@mui/material';
import { CalendarToday as CalendarTodayIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import ptLocale from 'date-fns/locale/pt';
import enLocale from 'date-fns/locale/en-US';

/**
 * Outlined button that opens a date range picker popover.
 * Button label is automatically formatted from startDate.
 */
const DateRangePopover = ({ startDate, endDate, onStartChange, onEndChange, startLabel, endLabel }) => {
  const { t, i18n } = useTranslation(['common', 'dashboard']);
  const [anchorEl, setAnchorEl] = useState(null);

  const currentLang = (i18n.language || 'pt').split('-')[0];
  const dateFnsLocale = currentLang === 'en' ? enLocale : ptLocale;

  const resolvedStartLabel = startLabel || t('common:from', 'Início');
  const resolvedEndLabel = endLabel || t('common:to', 'Fim');

  const isValidStart = startDate instanceof Date && !isNaN(startDate.getTime());
  const isValidEnd = endDate instanceof Date && !isNaN(endDate.getTime());
  const sameYear = isValidStart && isValidEnd && startDate.getFullYear() === endDate.getFullYear();
  const label = isValidStart && isValidEnd
    ? `${format(startDate, sameYear ? 'd MMM' : 'd MMM yyyy', { locale: dateFnsLocale })} – ${format(endDate, 'd MMM yyyy', { locale: dateFnsLocale })}`
    : isValidStart ? format(startDate, 'd MMM yyyy', { locale: dateFnsLocale }) : '—';

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          borderColor: 'divider',
          color: 'text.secondary',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
        }}
      >
        {label}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2 } }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
          <Stack
            spacing={2}
            sx={{ minWidth: 220 }}
          >
            <DatePicker
              label={resolvedStartLabel}
              value={startDate}
              onChange={onStartChange}
              maxDate={endDate ?? new Date()}
              renderInput={(params) => <TextField
                {...params}
                size="small"
              />}
            />
            <DatePicker
              label={resolvedEndLabel}
              value={endDate}
              onChange={onEndChange}
              minDate={startDate}
              maxDate={new Date()}
              renderInput={(params) => <TextField
                {...params}
                size="small"
              />}
            />
          </Stack>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

DateRangePopover.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
};

export default DateRangePopover;
